/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Bot, Lightbulb } from 'lucide-react';
import { styled } from '@mui/styles';

interface ToolUseProps {
  toolName?: string;
  toolInput: string;
}

const ToolUseAccordionSummary = styled(AccordionSummary)(() => ({
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '&.MuiAccordionSummary-root': {
    minHeight: '3rem',
  },
}));

const ToolUseAccordion = styled(Accordion)(() => ({
  '&.MuiAccordion-rounded': {
    borderRadius: '10px',
  },
}));

const ToolUseAccordionDetails = styled(AccordionDetails)(() => ({
  fontSize: '0.8rem',
  '&.MuiAccordionDetails-root': {
    padding: '0 8px',
  },
}));

export const ToolUse: React.FC<ToolUseProps> = ({ toolName, toolInput }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ my: 1 }}>
      <ToolUseAccordion defaultExpanded className="rounded-2xl">
        <ToolUseAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" component="div">
            <Bot className="inline text-sky-600" />{' '}
            {t('chat.tool.used', { name: toolName })}
          </Typography>
        </ToolUseAccordionSummary>
        <ToolUseAccordionDetails>
          <SyntaxHighlighter className="rounded-lg" language="json">
            {toolInput}
          </SyntaxHighlighter>
        </ToolUseAccordionDetails>
      </ToolUseAccordion>
    </Box>
  );
};

interface ToolUseObservationProps {
  toolName?: string;
  toolObservation: string;
}

export const ToolUseObservation: React.FC<ToolUseObservationProps> = ({
  toolName,
  toolObservation,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ my: 1 }}>
      <ToolUseAccordion className="rounded-2xl">
        <ToolUseAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" component="div">
            <Lightbulb className="inline text-yellow-500" />{' '}
            {t('chat.tool.observation', { name: toolName })}
          </Typography>
        </ToolUseAccordionSummary>
        <ToolUseAccordionDetails>
          <pre className="rounded-lg bg-stone-100 whitespace-break-spaces p-2 mb-2">
            {toolObservation}
          </pre>
        </ToolUseAccordionDetails>
      </ToolUseAccordion>
    </Box>
  );
};
